import axios from 'axios'
import { api } from './client'

/** Public axios instance (no auth interceptor redirect) */
const publicApi = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
})

export interface RegistrationPayload {
  full_name: string
  email: string
  phone: string
  address: string
  organization_id: number
  employee_number: string
  // Endereço estruturado (D-UX3) — opcionais, retrocompat com `address` livre.
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  /**
   * Confirma a reabertura de um cadastro recusado (TASK-10658).
   *
   * Sem ele, um e-mail com cadastro recusado recebe 409 `reopen_required` — a tela
   * de revisao. Com ele, a linha anterior e reaberta. E no-op quando nao ha cadastro
   * anterior: ele confirma, nao autoriza.
   */
  confirm_reopen?: boolean
}

export interface OrganizationOption {
  id: number
  name: string
}

export interface PendingRegistration {
  id: number
  full_name: string
  email: string
  phone: string
  organization_id: number
  organization_name: string
  employee_number: string
  created_at: string
  /** Quantas vezes a pessoa reenviou depois de uma recusa (TASK-10659). */
  resubmissions: number
}

export interface RegistrationDetail {
  id: number
  full_name: string
  email: string
  phone: string
  address: string
  organization_id: number
  organization_name: string
  employee_number: string
  status: string
  rejection_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  assigned_role: string | null
  created_at: string
  audit_log: { action: string; performed_by: string; details: Record<string, unknown>; created_at: string }[]
}

export const registrationApi = {
  /**
   * Public: organizacoes do dropdown, a partir do e-mail (TASK-10487).
   *
   * POST, e nao GET, porque agora manda o e-mail: ate 08/09/2026 esta rota devolvia a
   * carteira de clientes inteira a qualquer um sem autenticacao. Fora do dominio a
   * resposta e lista vazia, nunca 403 — 403 contaria qual e o dominio certo.
   */
  listOrganizations: async (email: string) => {
    const res = await publicApi.post<{ organizations: OrganizationOption[] }>(
      '/v1/auth/register/organizations', { email },
    )
    return res.data.organizations
  },

  /** Public: submit registration */
  submit: async (payload: RegistrationPayload) => {
    const res = await publicApi.post<{ status: string; registration_id: number; message: string }>('/v1/auth/register', payload)
    return res.data
  },

  /** Gestor: list pending registrations */
  listPending: async () => {
    const res = await api.get<{ registrations: PendingRegistration[] }>('/v1/admin/registrations')
    return res.data.registrations
  },

  /** Gestor: get registration detail */
  getDetail: async (id: number) => {
    const res = await api.get<RegistrationDetail>(`/v1/admin/registrations/${id}`)
    return res.data
  },

  /** Gestor: approve registration */
  approve: async (id: number, roleCode: string, templateId: number) => {
    const res = await api.post<{ status: string; user_id: number; email: string; notifications?: { email: boolean; whatsapp: boolean } }>(`/v1/admin/registrations/${id}/approve`, { role_code: roleCode, template_id: templateId })
    return res.data
  },

  /** Gestor: reject registration */
  reject: async (id: number, reason: string) => {
    const res = await api.post<{ status: string; email: string }>(`/v1/admin/registrations/${id}/reject`, { reason })
    return res.data
  },
}
